/**
 * Stowing and retrieving: the two moments where an object leaves the world for a pocket
 * and comes back.
 *
 * A stowed item is **disabled, not unmounted** (design D3). Unmounting would mean the
 * store had to remember where to put it back, and where things are is the physics
 * world's business, never the store's (architectural rule 3). So the body is handed
 * back the way a drop hands it back — dynamic again, its own collision groups, no
 * velocity — and then switched off: out of the physics world entirely, unhittable and
 * un-raycastable, while the component stays mounted and keeps its registration. Those
 * four steps are `parkBody`, shared with the attach path (design D7).
 *
 * Retrieving puts it at arm's length in front of the eye and lets `CarryController` take
 * over on the next frame, exactly as it does after a pick-up. The distance is the *close*
 * one on purpose: the carry controller's shape cast pushes a held object out to full
 * reach when there is room, and starting close is what stops a screwdriver appearing on
 * the far side of a hedge.
 */

import { useFrame, useThree } from '@react-three/fiber';
import { useRapier } from '@react-three/rapier';
import { useEffect, useRef } from 'react';
import { Vector3 } from 'three';
import { MIN_HOLD_DISTANCE } from '../game/carrying';
import { isStowable } from '../game/objectKinds';
import { isPlayable } from '../game/phase';
import { firstFreeSlot } from '../game/pocket';
import type { PlacedObject } from '../game/profile/buildGeometry';
import { consumePocketSlot, consumeStow, inputState } from '../input/inputState';
import { useGameStore } from '../store/gameStore';
import { getObjectBody } from './objectRegistry';
import { parkBody } from './parkBody';

const ZERO = { x: 0, y: 0, z: 0 };

type Props = {
  /** The level's objects, which is where a held id is turned back into a kind. */
  objects: readonly PlacedObject[];
};

export function PocketController({ objects }: Props): null {
  const camera = useThree((state) => state.camera);
  const { rapier } = useRapier();
  const pocket = useGameStore((store) => store.pocket);

  const directionRef = useRef(new Vector3());
  const pointRef = useRef(new Vector3());
  /** What this controller has switched off, so it can switch it back on again. */
  const disabledRef = useRef(new Set<string>());

  // Anything that empties the pocket without going through a retrieval — a restart, a
  // portal — would otherwise leave a body disabled with nothing holding its id. It comes
  // back where it was stowed and falls; losing it altogether is the alternative.
  useEffect(() => {
    const held = new Set(pocket.flatMap((item) => (item ? [item.id] : [])));
    for (const id of disabledRef.current) {
      if (held.has(id)) continue;
      getObjectBody(id)?.setEnabled(true);
      disabledRef.current.delete(id);
    }
  }, [pocket]);

  useFrame(() => {
    // Consumed before the playable check, as `InteractionSystem` consumes a tap: a press
    // made while paused is a moment that has passed, not one queued for the resume.
    const stowing = consumeStow(inputState);
    const slot = consumePocketSlot(inputState);

    const store = useGameStore.getState();
    if (!isPlayable(store.phase)) return;

    if (stowing) stow(store);
    if (slot !== null) retrieve(store, slot);
  });

  function stow(store: ReturnType<typeof useGameStore.getState>): void {
    const carriedId = store.carriedObjectId;
    if (carriedId === null) return;
    const kind = objects.find((object) => object.id === carriedId)?.kind;
    if (!kind || !isStowable(kind)) return;

    // Park the body only if the pocket will actually take it; otherwise `stow` is about
    // to refuse, and the item stays in the hands where the player left it.
    if (firstFreeSlot(store.pocket) !== null) {
      const body = getObjectBody(carriedId);
      if (!body) return;
      // The parking itself lives in `parkBody`, shared with the attach path (design D7),
      // so an item that comes back out of a pocket and one that comes off a robot dog
      // cannot end up behaving differently.
      parkBody(body, rapier.RigidBodyType.Dynamic);
      disabledRef.current.add(carriedId);
    }

    store.stow(kind);
  }

  function retrieve(store: ReturnType<typeof useGameStore.getState>, slot: number): void {
    if (store.carriedObjectId !== null) return;
    const item = store.pocket[slot];
    if (!item) return;
    const body = getObjectBody(item.id);
    // No body means the level it belongs to is gone; leave it in the pocket rather than
    // reporting a hand-over that did not happen.
    if (!body) return;

    const direction = camera.getWorldDirection(directionRef.current);
    const point = pointRef.current
      .copy(direction)
      .multiplyScalar(MIN_HOLD_DISTANCE)
      .add(camera.position);

    body.setEnabled(true);
    body.setTranslation(point, true);
    body.setLinvel(ZERO, true);
    body.setAngvel(ZERO, true);
    disabledRef.current.delete(item.id);

    store.takeFromPocket(slot);
  }

  return null;
}
